import React, { useMemo } from 'react';
import { Checkbox } from 'antd';
import { Sun, HouseLine, BatteryCharging, Cube } from '@phosphor-icons/react';
import './oneButton-css/DealHealthQuotesModal.css';

export interface QuotesCategoryQuote {
    id: string;
    partner: string;
    product: string;
    monthly: number;
    pricingMethod?: string;
    solarRate?: number;
    grossPpw?: number;
    epc?: number | null;
    systemSizeKw?: number;
    productionKwh?: number;
    Roof_Square_Override?: number;
    Price_Per_Sqft?: number;
    fnanceMethod?: string;
    quoteCreatedStr?: string | null;
    clonedQuoteId?: number;
    isMovedToContract?: boolean;
    batteryManufacturer?: string;
    batteryQuantity?: number;
}

export type QuotesCategoryColumnEntry = {
    quote: QuotesCategoryQuote;
    footer?: React.ReactNode;
    selected?: boolean;
    onToggleSelect?: (checked: boolean) => void;
};

/** Product category display order (matches financing grid / quotes tabs). */
const CATEGORY_ORDER: string[] = [
    'Solar',
    'Roof',
    'Battery',
    'Solar + Roof',
    'Battery + Roof',
    'Solar + Battery',
    'Solar + Battery + Roof',
];

/** Normalize API / quote product strings to a single category label. */
export function normalizeDealHealthCategory(product: string | null | undefined): string {
    const t = String(product ?? '').trim();
    if (!t) return 'Other';

    const lower = t.toLowerCase().replace(/\s+/g, '');

    if (lower === 'solarbatteryroof' || lower === 'solar+battery+roof' || lower === 'solarroofbattery') {
        return 'Solar + Battery + Roof';
    }
    if (lower === 'solarbattery' || lower === 'solar+battery') return 'Solar + Battery';
    if (lower === 'solarroof' || lower === 'solar+roof') return 'Solar + Roof';
    if (lower === 'batteryroof' || lower === 'battery+roof') return 'Battery + Roof';
    if (lower === 'roofonly' || lower === 'r' || lower === 'ro') return 'Roof';
    if (lower === 'batteryonly') return 'Battery';
    if (lower === 'solar') return 'Solar';
    if (lower === 'roof') return 'Roof';
    if (lower === 'battery') return 'Battery';

    const plusNormalized = t.replace(/\s*\+\s*/g, ' + ');
    if (CATEGORY_ORDER.includes(plusNormalized)) return plusNormalized;
    if (CATEGORY_ORDER.includes(t)) return t;

    return plusNormalized || t;
}

function categorySortIndex(category: string): number {
    const idx = CATEGORY_ORDER.indexOf(category);
    return idx === -1 ? CATEGORY_ORDER.length : idx;
}

function productCategoryIncludes(product: string, component: 'Solar' | 'Roof' | 'Battery'): boolean {
    return normalizeDealHealthCategory(product).toLowerCase().includes(component.toLowerCase());
}

function categoryIcons(category: string): React.ReactNode {
    const c = category.toLowerCase();
    const icons: React.ReactNode[] = [];
    if (c.includes('solar')) icons.push(<Sun key="solar" size={16} weight="duotone" aria-hidden />);
    if (c.includes('roof')) icons.push(<HouseLine key="roof" size={16} weight="duotone" aria-hidden />);
    if (c.includes('battery')) icons.push(<BatteryCharging key="battery" size={16} weight="duotone" aria-hidden />);
    if (icons.length === 0) return <Cube size={18} weight="duotone" aria-hidden />;
    return <span className="obd-deal-health-quotes-grid__column-icon-stack">{icons}</span>;
}

function formatMoney(value: number | null | undefined, fractionDigits = 2): string {
    if (value == null || Number.isNaN(value)) return '—';
    return `$${value.toLocaleString(undefined, {
        minimumFractionDigits: fractionDigits,
        maximumFractionDigits: fractionDigits,
    })}`;
}

export function formatQuotesCategoryPartner(partner: string): string {
    const p = (partner || '').trim();
    if (p.toLowerCase() === 'goodleap') return 'Good Leap';
    if (p.toLowerCase() === 'lightreach') return 'Light Reach';
    if (p.toLowerCase() === 'igs') return 'IGS';
    if (p.toLowerCase() === 'cash') return 'Cash';
    return p;
}

function partnerSortKey(partner: string): string {
    return formatQuotesCategoryPartner(partner).toLowerCase();
}

type QuoteMetric = { label: string; value: string };

function formatSystemSizeKw(value: number | undefined): string {
    if (typeof value !== 'number' || Number.isNaN(value)) return '—';
    return `${value.toFixed(2)} kW`;
}

function formatProductionKwh(value: number | undefined): string {
    if (typeof value !== 'number' || Number.isNaN(value)) return '—';
    return `${value.toLocaleString()} kWh`;
}

/** Metric rows aligned with Commerce Hub quote cards (`QuotesContractsCard`). */
function getQuoteMetrics(quote: QuotesCategoryQuote): QuoteMetric[] {
    const metrics: QuoteMetric[] = [];
    const product = quote.product ?? '';
    const isCash = quote.partner.toLowerCase() === 'cash';

    if (productCategoryIncludes(product, 'Solar')) {
        if (!isCash) {
            metrics.push({ label: 'Solar rate', value: formatMoney(quote.solarRate, 3) });
        }
        metrics.push({
            label: isCash ? 'Desired PPW' : 'Gross PPW',
            value: formatMoney(quote.grossPpw),
        });
    }
    if (productCategoryIncludes(product, 'Roof')) {
        metrics.push({
            label: 'Roof squares',
            value:
                quote.Roof_Square_Override != null
                    ? `${quote.Roof_Square_Override.toLocaleString(undefined, {
                          minimumFractionDigits: 2,
                          maximumFractionDigits: 2,
                      })} sq.`
                    : '—',
        });
        metrics.push({ label: 'Price / sqft', value: formatMoney(quote.Price_Per_Sqft) });
    }
    if (!isCash) {
        metrics.push({ label: 'Total EPC (Net)', value: formatMoney(quote.epc) });
    }
    const batteryOnly =
        productCategoryIncludes(product, 'Battery') &&
        !productCategoryIncludes(product, 'Solar') &&
        !productCategoryIncludes(product, 'Roof');
    if (batteryOnly && quote.batteryManufacturer) {
        metrics.push({ label: 'Manufacturer', value: quote.batteryManufacturer });
    }
    if (batteryOnly && quote.batteryQuantity != null && Number(quote.batteryQuantity) > 0) {
        metrics.push({ label: 'Battery count', value: String(quote.batteryQuantity) });
    }
    if (!batteryOnly) {
        metrics.push({ label: 'System size', value: formatSystemSizeKw(quote.systemSizeKw) });
        metrics.push({ label: 'Prod', value: formatProductionKwh(quote.productionKwh) });
    }
    if (quote.quoteCreatedStr) {
        metrics.push({ label: 'Quote created', value: quote.quoteCreatedStr });
    }
    if (quote.clonedQuoteId) {
        metrics.push({ label: 'Cloned from', value: `#${quote.clonedQuoteId}` });
    }

    return metrics;
}

function groupEntriesByPartner(
    entries: QuotesCategoryColumnEntry[],
): Array<{ partner: string; entries: QuotesCategoryColumnEntry[] }> {
    const map = new Map<string, QuotesCategoryColumnEntry[]>();
    entries.forEach((entry) => {
        const partner = formatQuotesCategoryPartner(entry.quote.partner);
        const list = map.get(partner) ?? [];
        list.push(entry);
        map.set(partner, list);
    });
    return Array.from(map.entries())
        .sort(([a], [b]) => a.localeCompare(b))
        .map(([partner, partnerEntries]) => ({ partner, entries: partnerEntries }));
}

function QuotesCategoryQuoteCard({
    entry,
    cardVariant = 'compact',
}: {
    entry: QuotesCategoryColumnEntry;
    cardVariant?: 'compact' | 'design';
}) {
    const { quote, footer, selected, onToggleSelect } = entry;
    const isCash = quote.partner.toLowerCase() === 'cash';
    const heroAmount = isCash ? formatMoney(quote.epc ?? quote.monthly) : formatMoney(quote.monthly);
    const heroSuffix = isCash ? 'EPC' : '/mo';
    const metrics = getQuoteMetrics(quote);
    const financeLabel = quote.fnanceMethod?.trim() || '—';
    const partnerLabel = formatQuotesCategoryPartner(quote.partner);
    const selectable = typeof onToggleSelect === 'function';
    const isDesign = cardVariant === 'design';
    const statusPillText = quote.isMovedToContract ? 'Moved to contract' : `#${quote.id} · Quote`;

    return (
        <article
            className={`obd-deal-health-quote${isDesign ? ' obd-deal-health-quote--design' : ''}${
                selected ? ' obd-deal-health-quote--selected' : ''
            }${quote.isMovedToContract ? ' obd-deal-health-quote--contract obd-deal-health-quote--hl' : ''}`}
        >
            {isDesign ? (
                <>
                    <div className="obd-deal-health-quote__head">
                        <div className="obd-deal-health-quote__head-copy">
                            <span className="obd-deal-health-quote__kicker">Partner</span>
                            <span className="obd-deal-health-quote__head-value">{partnerLabel}</span>
                            <span className="obd-deal-health-quote__kicker obd-deal-health-quote__kicker--secondary">
                                Finance method
                            </span>
                            <span className="obd-deal-health-quote__head-value obd-deal-health-quote__head-value--method" title={financeLabel}>
                                {financeLabel}
                            </span>
                        </div>
                    </div>
                    <div className="obd-deal-health-quote__hero">
                        <span className="obd-deal-health-quote__price">{heroAmount}</span>
                        <span className="obd-deal-health-quote__price-suffix">{heroSuffix}</span>
                    </div>
                    {metrics.length > 0 ? (
                        <dl className="obd-deal-health-quote__metrics">
                            {metrics.map((metric) => (
                                <div key={metric.label} className="obd-deal-health-quote__metric">
                                    <dt>{metric.label}</dt>
                                    <dd>{metric.value}</dd>
                                </div>
                            ))}
                        </dl>
                    ) : null}
                    <div className="obd-deal-health-quote__actions">
                        <div
                            className="obd-deal-health-quote__footer"
                            role="group"
                            aria-label="Quote status and actions"
                        >
                            <span className="obd-deal-health-quote__status-pill">
                                <span className="obd-deal-health-quote__status-dot" aria-hidden />
                                {statusPillText}
                            </span>
                            {footer}
                        </div>
                    </div>
                </>
            ) : (
                <>
                    {selectable ? (
                        <label className="obd-deal-health-quote__select-row">
                            <Checkbox checked={!!selected} onChange={(e) => onToggleSelect?.(e.target.checked)} />
                            <span className="obd-deal-health-quote__id">#{quote.id}</span>
                            <span className="obd-deal-health-quote__partner">{partnerLabel}</span>
                        </label>
                    ) : (
                        <div className="obd-deal-health-quote__select-row obd-deal-health-quote__select-row--static">
                            <span className="obd-deal-health-quote__id">#{quote.id}</span>
                            <span className="obd-deal-health-quote__partner">{partnerLabel}</span>
                        </div>
                    )}
                    <div className="obd-deal-health-quote__hero">
                        <span className="obd-deal-health-quote__price">{heroAmount}</span>
                        <span className="obd-deal-health-quote__price-suffix">{heroSuffix}</span>
                    </div>
                    <div className="obd-deal-health-quote__meta">
                        <span className="obd-deal-health-quote__finance" title={financeLabel}>
                            {financeLabel}
                        </span>
                        {quote.isMovedToContract ? (
                            <span className="obd-deal-health-quote__status">Moved to contract</span>
                        ) : null}
                    </div>
                    {metrics.length > 0 ? (
                        <dl className="obd-deal-health-quote__metrics">
                            {metrics.map((metric) => (
                                <div key={metric.label} className="obd-deal-health-quote__metric">
                                    <dt>{metric.label}</dt>
                                    <dd>{metric.value}</dd>
                                </div>
                            ))}
                        </dl>
                    ) : null}
                    {footer ? <div className="obd-deal-health-quote__actions">{footer}</div> : null}
                </>
            )}
        </article>
    );
}

export interface QuotesCategoryColumnViewProps {
    entries: QuotesCategoryColumnEntry[];
    emptyMessage?: string;
    className?: string;
    maxColumns?: number;
    /** Match Commerce Hub card grid styling (orange hero, blue status pill, etc.). */
    cardVariant?: 'compact' | 'design';
}

export const QuotesCategoryColumnView: React.FC<QuotesCategoryColumnViewProps> = ({
    entries,
    emptyMessage = 'No quotes available.',
    className,
    maxColumns = 4,
    cardVariant = 'compact',
}) => {
    const entriesByCategory = useMemo(() => {
        const grouped = new Map<string, QuotesCategoryColumnEntry[]>();
        entries.forEach((entry) => {
            const category = normalizeDealHealthCategory(entry.quote.product);
            const list = grouped.get(category) ?? [];
            list.push(entry);
            grouped.set(category, list);
        });
        grouped.forEach((list, category) => {
            grouped.set(
                category,
                [...list].sort(
                    (a, b) =>
                        partnerSortKey(a.quote.partner).localeCompare(partnerSortKey(b.quote.partner)) ||
                        Number(b.quote.id) - Number(a.quote.id),
                ),
            );
        });
        return grouped;
    }, [entries]);

    const categories = useMemo(
        () =>
            Array.from(entriesByCategory.keys()).sort(
                (a, b) => categorySortIndex(a) - categorySortIndex(b) || a.localeCompare(b),
            ),
        [entriesByCategory],
    );

    if (categories.length === 0) {
        return (
            <p className="obd-deal-health-quotes-grid__empty obd-deal-health-quotes-grid__empty--all">
                {emptyMessage}
            </p>
        );
    }

    const columnCount = Math.min(Math.max(categories.length, 1), maxColumns);

    return (
        <div
            className={`obd-deal-health-quotes-grid${className ? ` ${className}` : ''}`}
            role="region"
            aria-label="Quotes by product category"
            style={{ gridTemplateColumns: `repeat(${columnCount}, minmax(0, 1fr))` }}
        >
            {categories.map((category) => {
                const columnEntries = entriesByCategory.get(category) ?? [];
                const partnerGroups = groupEntriesByPartner(columnEntries);
                const showPartnerGroups = partnerGroups.length > 1;

                return (
                    <section
                        key={category}
                        className="obd-deal-health-quotes-grid__column"
                        aria-label={`${category} quotes`}
                    >
                        <header className="obd-deal-health-quotes-grid__column-head">
                            <span className="obd-deal-health-quotes-grid__column-icon">{categoryIcons(category)}</span>
                            <h3 className="obd-deal-health-quotes-grid__column-title">{category}</h3>
                            <span className="obd-deal-health-quotes-grid__column-count">
                                {columnEntries.length} quote{columnEntries.length === 1 ? '' : 's'}
                            </span>
                        </header>
                        <div className="obd-deal-health-quotes-grid__column-body">
                            {partnerGroups.map(({ partner, entries: partnerEntries }) => (
                                <div
                                    key={`${category}-${partner}`}
                                    className="obd-deal-health-quotes-grid__partner-group"
                                >
                                    {showPartnerGroups ? (
                                        <div className="obd-deal-health-quotes-grid__partner-label">
                                            {partner}
                                            <span>{partnerEntries.length}</span>
                                        </div>
                                    ) : null}
                                    {partnerEntries.map((entry) => (
                                        <QuotesCategoryQuoteCard
                                            key={entry.quote.id}
                                            entry={entry}
                                            cardVariant={cardVariant}
                                        />
                                    ))}
                                </div>
                            ))}
                        </div>
                    </section>
                );
            })}
        </div>
    );
};

export default QuotesCategoryColumnView;
